import { BadgeCheck, ExternalLink, Lock, ShieldCheck, UserCheck } from "lucide-react";
import { AUDIT_PROVIDER, AUDIT_REPORT_URL, KYC_PROVIDER, KYC_REPORT_URL } from "../config";
import { scrollToId } from "../lib/scroll";
import Reveal from "./Reveal";

const CHECKS = [
    { id: "kyc", icon: UserCheck, label: "KYC VERIFIED", sub: "TEAM IDENTITY" },
    { id: "audit", icon: ShieldCheck, label: "AUDIT PASSED", sub: "NO CRITICAL ISSUES" },
    { id: "lock", icon: Lock, label: "26% TEAM LOCKED", sub: "STREAMFLOW" },
    { id: "admin", icon: BadgeCheck, label: "ADMIN REVOKED", sub: "PUMP.FUN FEE SPLIT" },
];

export function TrustStrip() {
    return (
        <div data-testid="trust-strip" className="relative border-y border-white/5 bg-black/30">
            <div className="mx-auto grid max-w-7xl grid-cols-2 divide-white/5 px-4 sm:px-6 md:grid-cols-4 md:divide-x lg:px-10">
                {CHECKS.map((c) => (
                    <button
                        type="button"
                        key={c.id}
                        onClick={() => scrollToId(c.id === "lock" ? "tokenomics" : "dev")}
                        data-testid={`trust-strip-${c.id}`}
                        className="group flex items-center gap-3 px-3 py-4 text-left transition-colors hover:bg-white/[0.03] md:justify-center"
                    >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-green-400/40 bg-green-400/10 text-green-300 transition-transform group-hover:scale-110">
                            <c.icon className="h-4 w-4" />
                        </span>
                        <span className="flex flex-col">
                            <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-white">{c.label}</span>
                            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{c.sub}</span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

function StatusCard({ id, icon: Icon, title, sub, body, provider, url }) {
    return (
        <div data-testid={`compliance-${id}`} className="glass flex flex-col gap-4 rounded-2xl border-green-400/20 p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-200">
                        <Icon className="h-5 w-5" />
                    </span>
                    <div>
                        <div className="headline text-xl text-white">{title}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{sub}</div>
                    </div>
                </div>
                <span
                    data-testid={`compliance-${id}-status`}
                    className="flex items-center gap-1 rounded-full bg-green-400 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-black shadow-[0_0_18px_rgba(57,255,20,0.45)]"
                >
                    <BadgeCheck className="h-3 w-3" /> COMPLETED
                </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-300">{body}</p>
            <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3 font-mono text-[10px] uppercase tracking-widest">
                <span className="text-slate-500">{provider ? `BY ${provider}` : "PROVIDER: TO BE PUBLISHED"}</span>
                {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`compliance-${id}-report-link`} className="flex items-center gap-1 font-bold text-cyan-300 hover:text-cyan-100">
                        VIEW REPORT <ExternalLink className="h-3 w-3" />
                    </a>
                ) : (
                    <span data-testid={`compliance-${id}-report-pending`} className="text-slate-500">REPORT LINK COMING SOON</span>
                )}
            </div>
        </div>
    );
}

export default function Compliance() {
    return (
        <Reveal delay={0.1} className="mt-20">
            <div data-testid="compliance-section" className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
                <div className="glass overflow-hidden rounded-3xl border-white/10 p-2">
                    <img
                        src="/images/trust/kyc-audit-report.webp"
                        alt="BALD PIGEON security & compliance report — KYC completed, smart contract audit completed"
                        data-testid="compliance-report-image"
                        loading="lazy"
                        decoding="async"
                        className="w-full rounded-2xl"
                    />
                </div>
                <div className="flex flex-col gap-5">
                    <div>
                        <span className="kicker !text-green-300">SECURITY & COMPLIANCE</span>
                        <h3 className="headline mt-3 text-3xl text-white md:text-4xl">
                            KYC <span className="text-green-300">✓</span> AUDIT <span className="text-green-300">✓</span>
                        </h3>
                        <p className="mt-3 text-sm text-slate-400 md:text-base">
                            Pim did the paperwork so you don't have to guess. Team identity verified, contract audited, and
                            everything else on this page is on-chain.
                        </p>
                    </div>
                    <StatusCard
                        id="kyc"
                        icon={UserCheck}
                        title="KYC STATUS"
                        sub="CUSTOMER IDENTIFICATION"
                        body="The KYC process has been successfully completed. The identity of the project's team and key representatives has been verified."
                        provider={KYC_PROVIDER}
                        url={KYC_REPORT_URL}
                    />
                    <StatusCard
                        id="audit"
                        icon={ShieldCheck}
                        title="AUDIT STATUS"
                        sub="SMART CONTRACT AUDIT"
                        body="The smart contract audit has been successfully completed. No critical vulnerabilities were found in the audited code."
                        provider={AUDIT_PROVIDER}
                        url={AUDIT_REPORT_URL}
                    />
                </div>
            </div>
        </Reveal>
    );
}
